import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../context/AuthContext';
import { Heart, Trash2, TrendingUp, Tag, MapPin } from 'lucide-react';
import ProductCard from '../components/ProductCard';

export default function Wishlist() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/wishlist')
      .then(({ data }) => setProducts(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  const remove = async (productId) => {
    await api.delete(`/wishlist/${productId}`);
    setProducts(p => p.filter(x => x.id !== productId));
  };

  // Sidebar stats
  const totalValue = products.reduce((s, p) => s + (p.price || 0), 0);
  const avgPrice = products.length ? totalValue / products.length : 0;
  const conditions = products.reduce((acc, p) => { acc[p.condition] = (acc[p.condition] || 0) + 1; return acc; }, {});
  const provinces = [...new Set(products.map(p => p.province).filter(Boolean))];

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="animate-pulse grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => <div key={i} className="h-64 bg-gray-200 rounded-xl" />)}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Heart className="w-6 h-6 text-primary fill-primary" />
        <h1 className="text-2xl font-bold">Mi Lista de Deseos</h1>
        <span className="text-gray-500 text-sm">({products.length} productos)</span>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-16">
          <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 text-lg mb-4">Tu lista de deseos está vacía</p>
          <Link to="/products" className="btn-primary inline-block">Explorar Productos</Link>
        </div>
      ) : (
        <>
        <div className="flex gap-3 mb-4 md:hidden text-sm bg-white border rounded-xl p-4">
          <div className="flex-1 text-center">
            <p className="text-gray-500 text-xs">Guardados</p>
            <p className="font-bold">{products.length}</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center">
            <p className="text-gray-500 text-xs">Valor total</p>
            <p className="font-bold">₡{totalValue.toLocaleString('es-CR')}</p>
          </div>
          <div className="w-px bg-gray-200" />
          <div className="flex-1 text-center">
            <p className="text-gray-500 text-xs">Promedio</p>
            <p className="font-bold">₡{Math.round(avgPrice).toLocaleString('es-CR')}</p>
          </div>
        </div>
        <div className="flex gap-6 items-start">
          {/* Products grid */}
          <div className="flex-1 grid grid-cols-2 md:grid-cols-3 gap-4">
            {products.map(product => (
              <div key={product.id} className="relative">
                <ProductCard product={{ ...product, in_wishlist: true }} />
                <button
                  onClick={() => remove(product.id)}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow hover:bg-red-600 z-10"
                  title="Quitar de favoritos"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Sidebar */}
          <aside className="hidden md:flex flex-col gap-4 w-56 lg:w-64 shrink-0">
            {/* Summary */}
            <div className="bg-white rounded-xl border p-5 space-y-4">
              <h2 className="font-bold text-lg">Resumen</h2>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Heart className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Total guardados</p>
                  <p className="font-bold text-lg">{products.length}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Valor total</p>
                  <p className="font-bold text-lg">₡{totalValue.toLocaleString('es-CR')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Tag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Precio promedio</p>
                  <p className="font-bold text-lg">₡{Math.round(avgPrice).toLocaleString('es-CR')}</p>
                </div>
              </div>
            </div>

            {/* Condition breakdown */}
            {Object.keys(conditions).length > 0 && (
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-semibold mb-3">Condición</h3>
                <div className="space-y-2">
                  {Object.entries(conditions).map(([cond, count]) => (
                    <div key={cond} className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">{cond === 'NEW' ? 'Nuevo' : 'Usado'}</span>
                      <span className="font-semibold bg-gray-100 px-2 py-0.5 rounded-full">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Provinces */}
            {provinces.length > 0 && (
              <div className="bg-white rounded-xl border p-5">
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-400" /> Provincias
                </h3>
                <div className="flex flex-wrap gap-1.5">
                  {provinces.map(p => (
                    <span key={p} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {p.replace('_', ' ')}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <Link to="/products" className="btn-primary text-center text-sm py-2.5">
              + Seguir explorando
            </Link>
          </aside>
        </div>
        </>
      )}
    </div>
  );
}
